import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  VERSION_PATH,
  isBannerDue,
  parseVersion,
  shouldReloadOnNavigate,
  updateGuard,
  updateStatus,
  type UpdateState,
} from '@utils/app-update';

const CHECK_INTERVAL_MS = 3 * 60 * 1000;
// A stale write ages out without emitting anything, so busy is also re-read on
// a timer rather than only when the guard changes.
const BUSY_RECHECK_MS = 5000;

/**
 * Keeps the update prompt back while `active`, for work that is not a write in
 * flight but would still be lost to a reload — an AI extraction being watched.
 */
export const useHoldAppUpdate = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    return updateGuard.hold();
  }, [active]);
};

/**
 * The key the extraction screen defers under, and the page arriving clears.
 *
 * A run finishing is not the moment to ask for a reload: the user is still on
 * the completion screen and has not yet reached the draft it produced, so a
 * reload drops them back where they started with nothing to show for the wait.
 * The screen that knows the run finished unmounts on the way to the draft, so
 * it hands the wait over by name rather than holding it itself.
 */
export const EXTRACTION_HANDOVER = 'extraction-handover';

/**
 * Long enough to cover a slow draft page, short enough that a user who goes
 * somewhere else entirely is not left on a stale build indefinitely.
 */
export const HANDOVER_TTL_MS = 2 * 60 * 1000;

/** Defers the prompt past the navigation that follows, while `active`. */
export const useDeferAppUpdateAcrossNavigation = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    updateGuard.defer(EXTRACTION_HANDOVER, HANDOVER_TTL_MS);
    // Refreshed on the way out, so the window measures the navigation rather
    // than however long the user left the finished screen sitting open first —
    // otherwise a run read at leisure hands over an already-expired deferral
    // and the prompt lands on the click that was meant to open the draft.
    return () => updateGuard.defer(EXTRACTION_HANDOVER, HANDOVER_TTL_MS);
  }, [active]);
};

/**
 * Moves the tab onto a newer build when the user goes to another page, and
 * ends the extraction handover once they have landed somewhere else.
 *
 * Mounted inside the router, because the modal is rendered outside it and
 * cannot watch the location. Keyed on the pathname: a query or hash change is
 * a filter on the same page, not a page the user left. Draft tabs switch
 * inside the page, so editing a draft never gets here.
 *
 * The router has already pushed the new URL, so a reload loads the page the
 * user clicked, from the new build; `history.state` survives it. Leaving a
 * page already unmounts whatever was typed into it, so nothing more is lost.
 *
 * Busy is read before the handover is cleared: the navigation from a finished
 * extraction to its draft is the one the deferral exists for, so it stays a
 * plain navigation and the one after it reloads. The extraction screen
 * re-opens the deferral as it unmounts, and React runs that cleanup before
 * this effect, so it is already open here.
 */
export const useAppUpdateOnNavigate = (pathname: string) => {
  const arrivedAt = useRef(pathname);
  useEffect(() => {
    if (pathname === arrivedAt.current) return;
    arrivedAt.current = pathname;
    if (shouldReloadOnNavigate(updateStatus.getState(), updateGuard.isBusy()) && updateStatus.claimReload()) {
      window.location.reload();
      return;
    }
    updateGuard.clearDeferral(EXTRACTION_HANDOVER);
  }, [pathname]);
};

/** Polls /version.json for as long as it is mounted. Mount it once. */
export const useAppUpdateCheck = () => {
  useEffect(() => {
    // Dev has no version.json and HMR keeps the tab current anyway.
    if (!import.meta.env.PROD) return;

    // Keeps polling after a newer build is found: a later one may be required.
    const check = async () => {
      try {
        const response = await fetch(`${VERSION_PATH}?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;
        const deployed = parseVersion(await response.json());
        if (deployed) updateStatus.report(deployed);
      } catch {
        // Offline, or the SPA fallback answered with HTML: try again next time.
      }
    };
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    void check();
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, []);
};

export const useAppUpdateState = (): UpdateState => useSyncExternalStore(updateStatus.subscribe, updateStatus.getState);

/**
 * True once `due()` holds while nothing in progress would be lost by
 * reloading. Latched: once up, a background write starting behind it must not
 * make it flicker away.
 *
 * `due` is re-read on a timer as well as on guard changes: a stale write ages
 * out without emitting, and the banner falls due by the clock alone.
 */
const useShownWhenIdle = (due: () => boolean, watching: boolean): boolean => {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!watching || shown) return;

    const showIfIdle = () => {
      if (due() && !updateGuard.isBusy()) setShown(true);
    };
    showIfIdle();
    const unsubscribe = updateGuard.subscribe(showIfIdle);
    const timer = window.setInterval(showIfIdle, BUSY_RECHECK_MS);
    return () => {
      unsubscribe();
      window.clearInterval(timer);
    };
  }, [due, watching, shown]);

  return shown;
};

const requiredDue = () => updateStatus.getState().required;
const bannerDue = () => isBannerDue(updateStatus.getState(), Date.now());

/** True once a required build is deployed and nothing in progress would be lost by reloading. */
export const useAppUpdatePrompt = (): boolean => useShownWhenIdle(requiredDue, useAppUpdateState().required);

/**
 * True while the long-stale banner should be up. Hidden again once dismissed;
 * a build newer than the dismissed one brings it back.
 */
export const useAppUpdateBanner = (): boolean => {
  const state = useAppUpdateState();
  const shown = useShownWhenIdle(bannerDue, state.deployed !== null && !state.required);
  return shown && !state.dismissed && !state.required;
};
